import { useMemo } from 'react';

import { useSellerSetting } from '../../../hooks/use-seller-setting';
import { SellerSettings } from '../../../enums/seller-settings/seller-settings';
import { getCurrentDateTime } from '../../../services/datetime-service';
import {
  PLAN_CODE_TO_BILLING_PLAN_ID,
  PLAN_CODE_TO_TIER_NAME,
  type PlanTrialHistory,
} from '../billing-plan-trial-banner/plan-trial-constants';

type ExpiredPlanTrialData = {
  isTrialExpired: boolean;
  expiredPlanCode: string;
  expiredBillingPlanId: number;
  expiredPlanTierName: string;
};

const DEFAULT_EXPIRED_PLAN_TRIAL_DATA: ExpiredPlanTrialData = {
  isTrialExpired: false,
  expiredPlanCode: '',
  expiredBillingPlanId: 0,
  expiredPlanTierName: '',
};

/**
 * Hook to find expired plan trial info from the PlanFeaturesTrialInfo seller setting.
 * Returns data about the most recently expired trial.
 */
export const useGetExpiredPlanTrialData = (): ExpiredPlanTrialData => {
  const planTrialInfoJson = useSellerSetting(SellerSettings.planFeaturesTrialInfo);

  return useMemo(() => {
    if (!planTrialInfoJson) {
      return DEFAULT_EXPIRED_PLAN_TRIAL_DATA;
    }

    try {
      const planTrialHistory: PlanTrialHistory = JSON.parse(planTrialInfoJson);
      const now = getCurrentDateTime();

      // Find the most recently expired trial (where TrialEndUtc < now)
      let expiredPlanCode: string | null = null;
      let latestExpiredDate: Date | null = null;

      for (const [planCode, trialInfo] of Object.entries(planTrialHistory)) {
        const trialEndDate = new Date(trialInfo.TrialEndUtc);
        if (trialEndDate < now.toJSDate()) {
          // If we have multiple expired trials, use the one that expired most recently
          if (!latestExpiredDate || trialEndDate > latestExpiredDate) {
            expiredPlanCode = planCode;
            latestExpiredDate = trialEndDate;
          }
        }
      }

      if (!expiredPlanCode) {
        return DEFAULT_EXPIRED_PLAN_TRIAL_DATA;
      }

      const expiredPlanTierName = PLAN_CODE_TO_TIER_NAME[expiredPlanCode] ?? '';

      return {
        isTrialExpired: true,
        expiredPlanCode,
        expiredBillingPlanId: PLAN_CODE_TO_BILLING_PLAN_ID[expiredPlanCode] ?? 0,
        expiredPlanTierName,
      };
    } catch {
      return DEFAULT_EXPIRED_PLAN_TRIAL_DATA;
    }
  }, [planTrialInfoJson]);
};
